import React, { useState, useEffect } from 'react';
import { Phone, Mail, Menu, X } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { mockData } from '../mockData';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'
    }`}>
      {/* Top Bar */}
      <div className="bg-[#1e3a5f] text-white py-2">
        <div className="container mx-auto px-4 flex justify-between items-center text-sm">
          <div className="flex items-center gap-6">
            <a href={`tel:${mockData.company.phone}`} className="flex items-center gap-2 hover:text-red-400 transition-colors">
              <Phone size={14} />
              <span>{mockData.company.phone}</span>
            </a>
            <a href={`mailto:${mockData.company.email}`} className="hidden sm:flex items-center gap-2 hover:text-red-400 transition-colors">
              <Mail size={14} />
              <span>{mockData.company.email}</span>
            </a>
          </div>
          <div className="text-xs hidden md:block">
            {mockData.company.serviceAreas}
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="container mx-auto px-4 py-5">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <div className="flex items-center cursor-pointer transition-transform duration-300 hover:scale-105" onClick={() => {
            navigate('/');
            setTimeout(() => window.scrollTo(0, 0), 100);
          }}>
            <img 
              src={mockData.company.logo} 
              alt="ShieldComply Logo" 
              className="h-16 md:h-20 lg:h-24 w-auto object-contain"
            />
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {location.pathname === '/' ? (
              <>
                <button onClick={() => scrollToSection('services')} className="text-gray-700 hover:text-[#dc2626] font-medium transition-colors">
                  Services
                </button>
                <button onClick={() => scrollToSection('industries')} className="text-gray-700 hover:text-[#dc2626] font-medium transition-colors">
                  Industries
                </button>
                <button onClick={() => scrollToSection('why-choose-us')} className="text-gray-700 hover:text-[#dc2626] font-medium transition-colors">
                  Why Choose Us
                </button>
              </>
            ) : (
              <button onClick={() => navigate('/')} className="text-gray-700 hover:text-[#dc2626] font-medium transition-colors">
                Home
              </button>
            )}
            <button onClick={() => navigate('/online-courses')} className="text-gray-700 hover:text-[#dc2626] font-medium transition-colors">
              Online Courses
            </button>
            <button 
              onClick={() => {
                if (location.pathname !== '/') {
                  navigate('/?scrollTo=contact');
                } else {
                  scrollToSection('contact');
                }
              }} 
              className="bg-[#dc2626] text-white px-6 py-2.5 rounded-md hover:bg-[#b91c1c] transition-all duration-300 font-medium shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
            >
              Get Quote
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="lg:hidden text-gray-700 p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden mt-4 py-4 border-t border-gray-200 animate-in slide-in-from-top">
            <div className="flex flex-col gap-4">
              {location.pathname === '/' ? (
                <>
                  <button onClick={() => scrollToSection('services')} className="text-gray-700 hover:text-[#dc2626] font-medium transition-colors text-left py-2">
                    Services
                  </button>
                  <button onClick={() => scrollToSection('industries')} className="text-gray-700 hover:text-[#dc2626] font-medium transition-colors text-left py-2">
                    Industries
                  </button>
                  <button onClick={() => scrollToSection('why-choose-us')} className="text-gray-700 hover:text-[#dc2626] font-medium transition-colors text-left py-2">
                    Why Choose Us
                  </button>
                </>
              ) : (
                <button onClick={() => navigate('/')} className="text-gray-700 hover:text-[#dc2626] font-medium transition-colors text-left py-2">
                  Home
                </button>
              )}
              <button onClick={() => navigate('/online-courses')} className="text-gray-700 hover:text-[#dc2626] font-medium transition-colors text-left py-2">
                Online Courses
              </button>
              <button 
                onClick={() => {
                  if (location.pathname !== '/') {
                    navigate('/?scrollTo=contact');
                  } else {
                    scrollToSection('contact');
                  }
                  setIsMobileMenuOpen(false);
                }}
                className="bg-[#dc2626] text-white px-6 py-3 rounded-md hover:bg-[#b91c1c] transition-colors font-medium text-left"
              >
                Get Quote
              </button>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};
